import { useCallback, useRef, useState } from 'react';

import { DOODLE_COLORS, DOODLE_ERASER_SIZES, DOODLE_SIZES } from '../../../../doodle-editor/doodle-config';
import { hasRenderedContent } from './stroke-utils';

export { DOODLE_COLORS, DOODLE_ERASER_SIZES, DOODLE_SIZES } from '../../../../doodle-editor/doodle-config';

export type DoodleTool = 'brush' | 'eraser';

export interface DoodleStroke {
	points: number[][];
	color: string;
	size: number;
	tool: DoodleTool;
}

interface StrokeHistory {
	strokes: DoodleStroke[];
	redoStack: DoodleStroke[];
}

export const useDoodle = () => {
	const [tool, setTool] = useState<DoodleTool>('brush');
	const [color, setColor] = useState<string>(DOODLE_COLORS[0].hex);
	const [size, setSize] = useState<number>(DOODLE_SIZES[3].value);
	const [eraserSize, setEraserSize] = useState<number>(DOODLE_ERASER_SIZES[2].value);
	const [history, setHistory] = useState<StrokeHistory>({ strokes: [], redoStack: [] });
	const [currentStroke, setCurrentStroke] = useState<DoodleStroke | null>(null);
	const currentStrokeRef = useRef<DoodleStroke | null>(null);

	const startStroke = useCallback(
		(point: number[]) => {
			setHistory((h) => ({ strokes: h.strokes, redoStack: [] }));
			const stroke: DoodleStroke = { points: [point], color, size: tool === 'eraser' ? eraserSize : size, tool };
			currentStrokeRef.current = stroke;
			setCurrentStroke(stroke);
		},
		[color, eraserSize, size, tool]
	);

	const addPoint = useCallback((point: number[]) => {
		const prev = currentStrokeRef.current;
		if (!prev) return;
		const updated = { ...prev, points: [...prev.points, point] };
		currentStrokeRef.current = updated;
		setCurrentStroke(updated);
	}, []);

	const endStroke = useCallback(() => {
		const completed = currentStrokeRef.current;
		currentStrokeRef.current = null;
		setCurrentStroke(null);
		if (completed) {
			setHistory((h) => ({ strokes: [...h.strokes, completed], redoStack: [] }));
		}
	}, []);

	const undo = useCallback(() => {
		setHistory((h) => {
			if (h.strokes.length === 0) return h;
			const removed = h.strokes[h.strokes.length - 1];
			return { strokes: h.strokes.slice(0, -1), redoStack: [...h.redoStack, removed] };
		});
	}, []);

	const redo = useCallback(() => {
		setHistory((h) => {
			if (h.redoStack.length === 0) return h;
			const restored = h.redoStack[h.redoStack.length - 1];
			return { strokes: [...h.strokes, restored], redoStack: h.redoStack.slice(0, -1) };
		});
	}, []);

	const clear = useCallback(() => {
		currentStrokeRef.current = null;
		setHistory({ strokes: [], redoStack: [] });
		setCurrentStroke(null);
	}, []);

	const { strokes, redoStack } = history;
	const canUndo = strokes.length > 0;
	const canRedo = redoStack.length > 0;
	const hasContent = hasRenderedContent(strokes);

	return {
		tool,
		setTool,
		color,
		setColor,
		size,
		setSize,
		eraserSize,
		setEraserSize,
		strokes,
		currentStroke,
		startStroke,
		addPoint,
		endStroke,
		undo,
		redo,
		clear,
		canUndo,
		canRedo,
		hasContent,
	};
};
